import { usePageLoading } from '@/components/layout/PageLoadingProvider'
import { ContentPagination } from '@/components/common/ContentPagination'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { publicAsset } from '@/components/navigation/navigationAssets'
import { useYouTubePlayerAnalytics } from '@/hooks/useYouTubePlayerAnalytics'
import {
  defaultYouTubePlaylistConfig,
  getYouTubeEmbedUrl,
  getYouTubePlaylistVideos,
  getYouTubeThumbnailUrl,
} from '@/lib/data/videos'
import type { YouTubeVideoCardItem } from '@/types/video'

interface ReporteriaVideoPanelProps {
  playlistId?: string
  featuredVideoId?: string
  apiKey?: string
  title?: string
  mobileTitle?: string
  variant?: 'default' | 'home'
  videos?: YouTubeVideoCardItem[]
}

const fallbackVideos: YouTubeVideoCardItem[] = [
  {
    id: 'featured-playlist-video',
    title: 'Cargando video de YouTube',
    dateLabel: 'Fecha pendiente de API',
    sourceLabel: 'YouTube',
    videoId: defaultYouTubePlaylistConfig.featuredVideoId,
    thumbnailUrl: getYouTubeThumbnailUrl(defaultYouTubePlaylistConfig.featuredVideoId),
  },
]

export function ReporteriaVideoPanel({
  playlistId = defaultYouTubePlaylistConfig.playlistId,
  featuredVideoId = defaultYouTubePlaylistConfig.featuredVideoId,
  apiKey = defaultYouTubePlaylistConfig.apiKey,
  title = 'Reportería ciudadana',
  mobileTitle,
  variant = 'default',
  videos,
}: ReporteriaVideoPanelProps) {
  const playIcon = publicAsset('icons/navigation/play.svg')
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [playlistVideos, setPlaylistVideos] = useState<YouTubeVideoCardItem[]>([])
  const [loadingVideos, setLoadingVideos] = useState(!videos?.length)
  usePageLoading(loadingVideos)

  useEffect(() => {
    let ignore = false

    if (videos?.length) {
      setPlaylistVideos(videos)
      setLoadingVideos(false)
      return () => {
        ignore = true
      }
    }

    getYouTubePlaylistVideos({ playlistId, featuredVideoId, apiKey })
      .then((items) => {
        if (!ignore) setPlaylistVideos(items)
      })
      .catch(() => {
        if (!ignore) setPlaylistVideos([])
      })
      .finally(() => { if (!ignore) setLoadingVideos(false) })

    return () => {
      ignore = true
    }
  }, [apiKey, featuredVideoId, playlistId, videos])

  const videoItems = useMemo(() => {
    const items = playlistVideos.length ? playlistVideos : fallbackVideos
    return items.map((item, index) => ({
      ...item,
      videoId: item.videoId ?? (index === 0 ? featuredVideoId : undefined),
      thumbnailUrl:
        item.thumbnailUrl ??
        getYouTubeThumbnailUrl(item.videoId ?? (index === 0 ? featuredVideoId : undefined)),
    }))
  }, [featuredVideoId, playlistVideos])
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null)
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [homePage, setHomePage] = useState(1)
  const [isCompactHome, setIsCompactHome] = useState(false)
  const selectedVideo = videoItems.find((item) => item.id === selectedVideoId) ?? videoItems[0]
  const canPlaySelected = Boolean(playlistId && selectedVideo?.videoId)
  const additionalVideos = videoItems.slice(1, 3)
  const carouselVideos = additionalVideos.length ? additionalVideos : videoItems.slice(0, 1)
  const carouselPosition = carouselVideos.length > 0 ? carouselIndex % carouselVideos.length : 0
  const visibleCarouselVideos = variant === 'home'
    ? videoItems.slice(1, 7).map((item, slotIndex) => ({ item, slotIndex }))
    : carouselVideos.length
      ? Array.from({ length: 2 }, (_, slotIndex) => ({
        item: carouselVideos[(carouselPosition + slotIndex) % carouselVideos.length],
        slotIndex,
      }))
      : []

  const homePageSize = isCompactHome ? 3 : 7
  const homePageCount = Math.max(1, Math.ceil(videoItems.length / homePageSize))
  const homeVideos = videoItems.slice((homePage - 1) * homePageSize, homePage * homePageSize)

  useYouTubePlayerAnalytics({
    active: selectedVideoId === selectedVideo.id && canPlaySelected,
    iframeRef,
    placement: 'reporteria-panel',
    videoId: selectedVideo.videoId,
    videoName: selectedVideo.title,
  })

  useEffect(() => {
    setCarouselIndex(0)
  }, [carouselVideos.length])

  useEffect(() => {
    if (variant !== 'home') return undefined

    const mediaQuery = window.matchMedia('(max-width: 900px)')
    const updateLayout = () => setIsCompactHome(mediaQuery.matches)
    updateLayout()
    mediaQuery.addEventListener('change', updateLayout)

    return () => mediaQuery.removeEventListener('change', updateLayout)
  }, [variant])

  useEffect(() => {
    setHomePage((currentPage) => Math.min(currentPage, homePageCount))
  }, [homePageCount])

  useEffect(() => {
    if (variant === 'home' || carouselVideos.length < 2) return undefined

    const interval = window.setInterval(() => {
      setCarouselIndex((currentIndex) => (currentIndex + 1) % carouselVideos.length)
    }, 6000)

    return () => {
      window.clearInterval(interval)
    }
  }, [carouselVideos.length, variant])

  if (!playlistId) {
    return (
      <section className="reporteria-video-panel">
        <p role="status">Configura VITE_YOUTUBE_PLAYLIST_ID para mostrar videos.</p>
      </section>
    )
  }

  const featureCard = (
    <article className="youtube-feature-card">
      <button
        type="button"
        className="youtube-feature-card__thumbnail"
        aria-label={
          canPlaySelected
            ? `Reproducir ${selectedVideo.title}`
            : `${selectedVideo.title}: pendiente de API`
        }
        disabled={!canPlaySelected}
        onClick={() => setSelectedVideoId(selectedVideo.id)}
      >
        {selectedVideoId === selectedVideo.id && canPlaySelected ? (
          <iframe
            key={selectedVideo.videoId}
            ref={iframeRef}
            title={selectedVideo.title}
            src={`${getYouTubeEmbedUrl({
              playlistId,
              featuredVideoId: selectedVideo.videoId,
            })}&autoplay=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <>
            {selectedVideo.thumbnailUrl ? (
              <img
                className="youtube-feature-card__poster"
                src={selectedVideo.thumbnailUrl}
                alt=""
                loading="lazy"
                decoding="async"
              />
            ) : null}
            <img
              className="youtube-feature-card__play"
              src={playIcon}
              alt=""
              loading="lazy"
              decoding="async"
            />
          </>
        )}
      </button>
      <h3>{selectedVideo.title}</h3>
      <div className="youtube-feature-card__meta">
        <strong>{selectedVideo.dateLabel ?? 'Pendiente de API'}</strong>
        <span>{selectedVideo.sourceLabel ?? 'YouTube · El Telégrafo'}</span>
      </div>
    </article>
  )

  if (variant === 'home') {
    return (
      <section className="reporteria-video-panel reporteria-video-panel--home" aria-labelledby="reporteria-video-title">
        <div className="reporteria-video-panel__player">
          <header className="reporteria-video-panel__header">
            <h2 id="reporteria-video-title">{title}</h2>
          </header>
          {featureCard}
        </div>

        <div className="reporteria-video-panel__catalog">
          <header className="reporteria-video-panel__catalog-header">
            <h3>Lista de entrevistas</h3>
            <Link to="/reporteria-ciudadana/videos">Ver todas&nbsp; →</Link>
          </header>
          <ContentPagination
            currentPage={homePage}
            pageCount={homePageCount}
            compact={isCompactHome}
            className="video-pagination"
            label="Páginas de entrevistas"
            onPageChange={setHomePage}
          />
          <div className="reporteria-video-panel__list" aria-label="Entrevistas recientes">
            <div className="youtube-video-carousel-track">
              {homeVideos.map((item) => (
                <button
                  type="button"
                  className="youtube-horizontal-card"
                  data-selected={item.id === selectedVideo.id}
                  aria-pressed={item.id === selectedVideo.id}
                  key={item.id}
                  disabled={!item.videoId}
                  onClick={() => setSelectedVideoId(item.id)}
                >
                  <span className="youtube-horizontal-card__thumbnail">
                    {item.thumbnailUrl ? (
                      <img
                        className="youtube-horizontal-card__poster"
                        src={item.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        decoding="async"
                      />
                    ) : null}
                    <img className="youtube-horizontal-card__play" src={playIcon} alt="" />
                  </span>
                  <span className="youtube-horizontal-card__body">
                    <small>{item.eyebrow ?? 'Playlist'}</small>
                    <strong>{item.title}</strong>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className={`reporteria-video-panel reporteria-video-panel--${variant}`} aria-labelledby="reporteria-video-title">
      <header className="reporteria-video-panel__header">
        <h2 id="reporteria-video-title">
          <span>{title}</span>
          {mobileTitle ? <span className="reporteria-video-panel__mobile-title">{mobileTitle}</span> : null}
        </h2>
        <Link to="/reporteria-ciudadana/videos">Ver todas -&gt;</Link>
      </header>

      {featureCard}

      <div
        className="reporteria-video-panel__list"
        id="reporteria-videos-list"
        aria-label="Carrusel vertical de videos recientes"
      >
        <div className="youtube-video-carousel-track">
          {visibleCarouselVideos.map(({ item, slotIndex }) => (
            <button
              type="button"
              className="youtube-horizontal-card youtube-horizontal-card--carousel"
              data-carousel-slot={slotIndex}
              key={`${carouselPosition}-${slotIndex}-${item.id}`}
              disabled={!item.videoId}
              onClick={() => setSelectedVideoId(item.id)}
            >
              <span className="youtube-horizontal-card__thumbnail">
                {item.thumbnailUrl ? (
                  <img
                    className="youtube-horizontal-card__poster"
                    src={item.thumbnailUrl}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                ) : null}
                <img
                  className="youtube-horizontal-card__play"
                  src={playIcon}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              </span>
              <span className="youtube-horizontal-card__body">
                <small>{item.eyebrow ?? 'Playlist'}</small>
                <strong>{item.title}</strong>
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
